import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonService } from '../../../../services/common';
import { SalesService } from '../../sales.service';
import { DocumentsService } from '../../../../services/common/documents.service';
import { ToasterService } from '../../../../services/common/toaster.service';

@Component({
    selector: 'app-booking-cancellation-details-modal',
    templateUrl: './booking-cancellation-details-modal.component.html',
    styleUrl: './booking-cancellation-details-modal.component.scss'
})
export class BookingCancellationDetailsModalComponent implements OnInit, OnChanges {
    @Input() isVisible: boolean = false;
    @Input() bookingId: any;
    @Input() bookingData: any; // Booking details from parent

    @Output() closeModal = new EventEmitter<void>();

    // Read-only data
    cancellationDetails: any = null;
    cancellationDocument: any = null;
    showDocumentPreview: boolean = false;
    isLoading: boolean = false;

    constructor(
        private commonService: CommonService,
        private salesService: SalesService,
        private documentsService: DocumentsService,
        private toasterService: ToasterService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        // Initial setup
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Fetch cancellation details when modal becomes visible
        if (changes['isVisible']?.currentValue === true && this.bookingId) {
            this.fetchCancellationDetails();
        }
    }

    /**
     * Fetch cancellation details from API
     */
    private fetchCancellationDetails(): void {
        this.isLoading = true;
        this.salesService.GetBookingCancellationDetails(this.bookingId).subscribe({
            next: (res) => {
                if (res && res?.result) {
                    this.cancellationDetails = res?.result;

                    // Fetch document if documentId exists
                    if (this.cancellationDetails?.documentId) {
                        this.fetchCancellationDocument();
                    } else {
                        this.isLoading = false;
                    }
                } else {
                    this.toasterService.showError('Failed to fetch cancellation details.');
                    this.isLoading = false;
                }
            },
            error: (err) => {
                this.toasterService.showError(err?.error?.message || 'Failed to fetch cancellation details.');
                this.isLoading = false;
            }
        });
    }

    /**
     * Fetch document bytes for preview
     */
    private fetchCancellationDocument(): void {
        const documentId = this.cancellationDetails?.documentId;

        if (!documentId) {
            this.isLoading = false;
            return;
        }

        this.documentsService.getDocumentBytesById(documentId).subscribe({
            next: (bytesResponse: any) => {
                try {
                    let base64String = bytesResponse;

                    if (bytesResponse instanceof Blob) {
                        const reader = new FileReader();
                        reader.onload = () => {
                            const result = reader.result as string;
                            base64String = result.includes(',') ? result.split(',')[1] : result;
                            this.cancellationDocument = {
                                id: documentId,
                                data: base64String,
                                mimeType: bytesResponse.type || 'image/jpeg'
                            };
                            this.isLoading = false;
                        };
                        reader.readAsDataURL(bytesResponse);
                    } else {
                        this.cancellationDocument = {
                            id: documentId,
                            data: base64String,
                            mimeType: 'image/jpeg'
                        };
                        this.isLoading = false;
                    }
                } catch (error) {
                    this.isLoading = false;
                }
            },
            error: (err: any) => {
                this.isLoading = false;
            }
        });
    }

    /**
     * Get status display with appropriate styling
     */
    getStatusClass(): string {
        if (this.cancellationDetails?.isApproved === true) {
            return 'status-approved';
        } else if (this.cancellationDetails?.isApproved === false) {
            return 'status-rejected';
        }
        return 'status-pending';
    }

    /**
     * Get status display text
     */
    getStatusText(): string {
        if (this.cancellationDetails?.isApproved === true) {
            return 'APPROVED';
        } else if (this.cancellationDetails?.isApproved === false) {
            return 'REJECTED';
        }
        return 'PENDING FOR APPROVAL';
    }

    /**
     * Open document preview
     */
    openDocumentPreview(): void {
        if (this.cancellationDocument) {
            this.showDocumentPreview = true;
        }
    }

    /**
     * Close preview modal
     */
    closePreview(): void {
        this.showDocumentPreview = false;
    }

    /**
     * Close cancellation details modal
     */
    onClose(): void {
        this.cancellationDetails = null;
        this.cancellationDocument = null;
        this.showDocumentPreview = false;
        this.closeModal.emit();
    }
}
